import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Clock, Users, Shield, MessageCircle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NAVY = '#004C54';
const NAVY_DEEP = '#003a40';
const GOLD = '#4FBCC7';
const CREAM = '#F5F3EE';

function OptInForm({ id, onDone, dataTestPrefix }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/coaches-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          source: 'coaches_reset',
        }),
      });
      if (r.ok) {
        const body = await r.json();
        onDone(body);
      } else {
        const b = await r.json().catch(() => ({}));
        setErr(b.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id={id} onSubmit={submit} className="space-y-3" data-testid={`${dataTestPrefix}-form`}>
      <Input
        placeholder="First name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        className="bg-white"
        data-testid={`${dataTestPrefix}-firstname`}
      />
      <Input
        type="email"
        placeholder="Email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="bg-white"
        data-testid={`${dataTestPrefix}-email`}
      />
      {err && (
        <p className="text-[13px] text-red-700" data-testid={`${dataTestPrefix}-error`}>{err}</p>
      )}
      <Button
        type="submit"
        disabled={loading || !firstName || !email}
        className="w-full font-bold h-12 text-[14px]"
        style={{ background: NAVY, color: '#fff' }}
        data-testid={`${dataTestPrefix}-submit`}
      >
        {loading ? 'Sending…' : 'Send Me the Free Protocol →'}
      </Button>
      <p className="text-[11.5px] text-center" style={{ color: '#666' }}>
        No spam. Unsubscribe anytime. We respect your inbox.
      </p>
    </form>
  );
}

export default function CoachesReset() {
  const navigate = useNavigate();

  const handleDone = (body) => {
    const pdfUrl = body?.pdf_url || '/coaches-reset.pdf';
    navigate(`/playbook/thank-you?pdf=${encodeURIComponent(pdfUrl)}`);
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="coaches-reset-page">
      <div style={{ background: NAVY, color: 'rgba(255,255,255,0.8)' }} className="text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 py-2 flex items-center justify-between">
          <Link to="/" className="font-mono-accent uppercase tracking-[0.18em] hover:text-white" data-testid="coaches-reset-back-home">
            ← apexathleticmind.com
          </Link>
          <a href="#second-cta" className="hidden sm:inline font-semibold hover:text-white" style={{ color: GOLD }} data-testid="coaches-reset-top-cta-link">
            Get the protocol →
          </a>
        </div>
      </div>

      <section style={{ background: CREAM }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20 grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-14 items-center">
          <div>
            <div
              className="font-mono-accent uppercase tracking-[0.26em] text-[10.5px] font-bold mb-5"
              style={{ color: GOLD }}
              data-testid="coaches-reset-eyebrow"
            >
              Free for coaches
            </div>
            <h1
              className="font-heading text-[2rem] sm:text-[2.8rem] md:text-[3.2rem] font-bold tracking-tight leading-[1.05] mb-5"
              style={{ color: NAVY }}
              data-testid="coaches-reset-hero-title"
            >
              Halftimes are won in the <span style={{ color: GOLD }}>first 30 seconds.</span>
            </h1>
            <p
              className="text-[16px] sm:text-[18px] leading-relaxed text-[#1a1a1a] mb-6 max-w-[560px]"
              data-testid="coaches-reset-hero-sub"
            >
              The 5-minute locker room protocol I run with state champions. Read the team. Reset the team.
              Re-engage the team. Coach-to-coach. No fluff.
            </p>
            <OptInForm id="coaches-reset-form-1" onDone={handleDone} dataTestPrefix="coaches-reset-hero" />
          </div>

          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(0,76,84,0.25)] mx-auto w-full max-w-[360px] aspect-[3/4]"
            style={{ background: CREAM }}
            data-testid="coaches-reset-cover-preview"
          >
            <div className="absolute inset-x-0 top-0 h-3" style={{ background: GOLD }} />
            <div className="absolute inset-x-0 bottom-0 h-[28%]" style={{ background: NAVY }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 pt-10 pb-[30%]">
              <div className="font-mono-accent uppercase tracking-[0.22em] text-[8.5px] font-bold mb-6" style={{ color: NAVY }}>
                Free Playbook · For Coaches
              </div>
              <h3 className="font-heading font-bold leading-[1.05] mb-4 text-[28px]" style={{ color: NAVY }}>
                The 5-Minute<br/>Locker Room Reset
              </h3>
              <p className="text-[11px] leading-relaxed" style={{ color: '#555' }}>
                Read. Reset. Re-engage.<br/>The halftime protocol that wins quarters.<br/>Coach-to-coach. No fluff.
              </p>
              <div className="w-12 h-[2px] mt-5" style={{ background: GOLD }} />
            </div>
            <div className="absolute inset-x-0 bottom-0 px-6 pb-5 text-center">
              <p className="font-heading font-bold text-[13px] text-white mb-1">By Coach Varsay Lewis, M.Ed.</p>
              <p className="text-[10px]" style={{ color: GOLD }}>Founder, Apex Athletic Mind</p>
              <p className="text-[9.5px] mt-1" style={{ color: 'rgba(255,255,255,0.8)' }}>apexathleticmind.com</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y" style={{ borderColor: 'rgba(0,76,84,0.1)' }}>
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div className="text-center mb-10">
            <div className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3" style={{ color: GOLD }}>
              What’s inside
            </div>
            <h2 className="font-heading text-[1.9rem] sm:text-[2.3rem] font-bold tracking-tight leading-tight" style={{ color: NAVY }} data-testid="coaches-reset-inside-title">
              What’s inside the protocol
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 max-w-[920px] mx-auto">
            {[
              {
                icon: Users,
                title: 'The 3-Zone Read',
                blurb: 'Green / Yellow / Red — know which team you walked in on before you open your mouth.',
              },
              {
                icon: Clock,
                title: 'The 5-Minute Map',
                blurb: 'Minute by minute. Read. Re-anchor. One truth. Individuals. Walk-out cue.',
              },
              {
                icon: Shield,
                title: 'Bounce-Back 3R',
                blurb: 'When the quarter went badly: Recognize. Reframe. Re-engage — before any X’s and O’s.',
              },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="rounded-xl p-6 border"
                  style={{ background: CREAM, borderColor: 'rgba(0,76,84,0.1)' }}
                  data-testid={`coaches-reset-inside-${card.title.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div
                    className="w-11 h-11 rounded-md flex items-center justify-center mb-4"
                    style={{ background: NAVY, color: GOLD }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading text-[16px] font-bold mb-1.5" style={{ color: NAVY }}>{card.title}</h3>
                  <p className="text-[13.5px] text-[#3a3a3a] leading-relaxed">{card.blurb}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ background: CREAM }}>
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <div
            className="rounded-2xl p-7 sm:p-10 border"
            style={{ background: 'white', borderColor: 'rgba(0,76,84,0.1)' }}
          >
            <div className="font-heading text-[44px] leading-none mb-2" style={{ color: GOLD }}>“</div>
            <p
              className="font-heading text-[20px] sm:text-[24px] font-bold leading-snug mb-4"
              style={{ color: NAVY }}
              data-testid="coaches-reset-pullquote"
            >
              Walking in and reading the zone in the first 30 seconds is the highest-leverage move you make
              all halftime. Mis-read it and your speech makes things worse.
            </p>
            <p className="text-[13px] font-mono-accent uppercase tracking-[0.18em] font-bold" style={{ color: GOLD }}>
              — Coach Varsay Lewis, M.Ed.
            </p>
          </div>

          <div className="mt-8 text-[15.5px] leading-relaxed text-[#1a1a1a] space-y-4">
            <p>
              The locker room at halftime isn’t one room. It’s three. Your job in the first 30 seconds is to
              figure out which one you walked into — Green, Yellow, or Red.
            </p>
            <p>
              Get the read right and the next 4 minutes write themselves. Get it wrong and your strategy talk
              lands in a flooded nervous system, and the team comes out worse than they went in. <strong>Reset
              first, then teach.</strong>
            </p>
            <p style={{ color: NAVY }} className="font-semibold">This protocol shows you how.</p>
          </div>
        </div>
      </section>

      <section id="second-cta" style={{ background: NAVY }} className="text-white">
        <div className="max-w-[640px] mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <div className="font-mono-accent uppercase tracking-[0.24em] text-[10px] font-bold mb-3" style={{ color: GOLD }}>
            Run it at your next halftime
          </div>
          <h2 className="font-heading text-[1.9rem] sm:text-[2.4rem] font-bold tracking-tight leading-tight mb-5" data-testid="coaches-reset-cta2-title">
            Get the protocol. Win the second half.
          </h2>
          <p className="text-[14.5px] text-white/75 leading-relaxed mb-7">
            Plus — when you opt in, I’ll send you a 2-week email series with the same mental performance toolkit,
            coach edition. Reply anytime — I read every email.
          </p>
          <div className="max-w-[420px] mx-auto">
            <OptInForm id="coaches-reset-form-2" onDone={handleDone} dataTestPrefix="coaches-reset-cta2" />
          </div>
        </div>
      </section>

      <footer style={{ background: NAVY_DEEP }} className="text-white/55 text-[12px]">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-2">
          <Link to="/" className="hover:text-white" data-testid="coaches-reset-footer-home">
            © Apex Athletic Mind
          </Link>
          <div className="flex gap-5 flex-wrap justify-center">
            <Link to="/quiz" className="hover:text-white" data-testid="coaches-reset-footer-quiz">Mindset Quiz</Link>
            <a href="mailto:volewis@apexathleticmind.com" className="hover:text-white">Contact</a>
            <Link to="/unsubscribe" className="hover:text-white">Unsubscribe</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { CoachesReset };
